// src/components/CreatePromptForm.js
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { createPrompt, upsertTagsForEntity } from "../util/db";
import { useAuth } from "../util/auth";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { fetchLambdaModels } from "../util/db"; 

function CreatePromptForm() {
  const auth = useAuth();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);

  const { register, handleSubmit, errors, reset } = useForm();

  useEffect(() => {
    fetchLambdaModels().then(setModels).catch((err) => {
      console.error("Failed to load models", err);
    });
  }, []);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      if (auth.user.role !== "in_good_standing") {
        throw new Error("You must be in good standing to add prompts.");
      }

      const selectedModel = models.find(
        (model) => model.id === parseInt(selectedModelId)
      );

      // 1) Create the prompt itself
      const [newPrompt] = await createPrompt({
        owner: auth.user.uid,
        title: data.title,
        content: data.content,
        default_model_id: selectedModel?.id || null,
        default_model_name: selectedModel?.model_name || null,
      });
      // newPrompt is the newly created DB row

      // 2) Tags: parse, limit 50
      const tags = parseCommaSeparated(data.tags);
      if (tags.length > 50) {
        throw new Error("Cannot exceed 50 tags");
      }

      // 3) Upsert those tags and link to prompt
      await upsertTagsForEntity({
        entityType: "prompt",
        entityId: newPrompt.id,
        tags,
      });

      reset(); // Clears form
      setSelectedModelId(null);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {errorMessage && (
        <Box mb={2}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Title"
            name="title"
            variant="outlined"
            size="small"
            fullWidth
            error={!!errors.title}
            helperText={errors.title?.message}
            inputRef={register({ required: "Please enter a title" })}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            select
            label="Default Model"
            name="default_model"
            variant="outlined"
            size="small"
            fullWidth
            SelectProps={{ native: true }}
            value={selectedModelId || ""}
            onChange={(e) => setSelectedModelId(e.target.value)}
          >
            <option value="">Select a model</option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.model_name}
              </option>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Content"
            name="content"
            variant="outlined"
            multiline
            rows={4}
            size="small"
            fullWidth
            error={!!errors.content}
            helperText={errors.content?.message}
            inputRef={register({ required: "Please enter prompt content" })}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Tags (comma-separated)"
            name="tags"
            variant="outlined"
            size="small"
            fullWidth
            inputRef={register}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={22} /> : "Save Prompt"}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

// Simple helper to parse a comma-separated string of tags
function parseCommaSeparated(text = "") {
  return text
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

export default CreatePromptForm;